import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, AlertTriangle, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { usePlatformTenants, PLATFORM_TENANTS_KEY } from '@/hooks/usePlatformTenants';
import { deleteTenant, type TenantListItem } from '@/services/platform-admin-api';

const SYSTEM_TENANT_SLUG = 'platform';

const TIER_LABELS: Record<string, string> = {
  FREE: 'Free',
  BASIC: 'Basic',
  PROFESSIONAL: 'Professional',
  ENTERPRISE: 'Enterprise',
};

const STATUS_STYLES: Record<string, string> = {
  active: 'bg-green-100 text-green-800',
  suspended: 'bg-red-100 text-red-800',
  trial: 'bg-blue-100 text-blue-800',
};

const TIER_OPTIONS = ['', 'FREE', 'BASIC', 'PROFESSIONAL', 'ENTERPRISE'] as const;
const STATUS_OPTIONS = ['', 'active', 'trial', 'suspended'] as const;

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('de-DE');
}

function formatNumber(n: number): string {
  return n.toLocaleString('de-DE');
}

const PAGE_SIZE = 10;

export function PlatformTenants() {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [tier, setTier] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [tenantToDelete, setTenantToDelete] = useState<TenantListItem | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteTenant(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PLATFORM_TENANTS_KEY });
      setTenantToDelete(null);
      setDeleteError(null);
    },
    onError: (err: unknown) => {
      setDeleteError(err instanceof Error ? err.message : 'Tenant konnte nicht gelöscht werden.');
    },
  });

  function handleDeleteClick(tenant: TenantListItem) {
    setDeleteError(null);
    setTenantToDelete(tenant);
  }

  function handleDeleteConfirm() {
    if (tenantToDelete) {
      deleteMutation.mutate(tenantToDelete.id);
    }
  }

  function handleDeleteCancel() {
    if (deleteMutation.isPending) return;
    setTenantToDelete(null);
    setDeleteError(null);
  }

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data, isLoading, isError, error, refetch } = usePlatformTenants({
    page,
    pageSize: PAGE_SIZE,
    search: search || undefined,
    tier: tier || undefined,
    status: status || undefined,
    sortBy: 'createdAt',
    sortOrder,
  });

  const totalPages = data ? Math.ceil(data.total / PAGE_SIZE) : 0;

  function handleTierChange(value: string) {
    setTier(value);
    setPage(1);
  }

  function handleStatusChange(value: string) {
    setStatus(value);
    setPage(1);
  }

  function handleCreatedAtSort() {
    setSortOrder((current) => (current === 'asc' ? 'desc' : 'asc'));
    setPage(1);
  }

  return (
    <div>
      <h1 className="text-2xl font-bold">Tenant-Verwaltung</h1>
      <p className="mt-2 text-muted-foreground">Alle Tenants der Plattform verwalten.</p>

      {/* Filters */}
      <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Suchen nach Name oder Slug..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-9"
          />
        </div>
        <select
          value={tier}
          onChange={(e) => handleTierChange(e.target.value)}
          className="h-10 rounded-md border border-input bg-background px-3 text-sm"
        >
          <option value="">Alle Tiers</option>
          {TIER_OPTIONS.filter(Boolean).map((t) => (
            <option key={t} value={t}>{TIER_LABELS[t] ?? t}</option>
          ))}
        </select>
        <select
          value={status}
          onChange={(e) => handleStatusChange(e.target.value)}
          className="h-10 rounded-md border border-input bg-background px-3 text-sm"
        >
          <option value="">Alle Status</option>
          {STATUS_OPTIONS.filter(Boolean).map((s) => (
            <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="mt-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              Tenants
              {data && (
                <span className="ml-2 text-sm font-normal text-muted-foreground">
                  ({formatNumber(data.total)} gesamt)
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="h-12 animate-pulse rounded bg-muted" />
                ))}
              </div>
            ) : isError ? (
              <div className="flex flex-col items-center gap-3 py-8">
                <div className="flex items-center gap-2 text-sm text-destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <span>{error instanceof Error ? error.message : 'Fehler beim Laden der Tenants'}</span>
                </div>
                <Button variant="outline" size="sm" onClick={() => refetch()}>
                  Erneut versuchen
                </Button>
              </div>
            ) : !data?.tenants.length ? (
              <p className="py-8 text-center text-sm text-muted-foreground">Keine Tenants gefunden.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full table-fixed text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="w-[26%] pb-2 font-medium">Name</th>
                      <th className="w-[11%] pb-2 font-medium">Tier</th>
                      <th className="w-[11%] pb-2 font-medium">Status</th>
                      <th className="w-[8%] pb-2 font-medium text-right">Users</th>
                      <th className="w-[8%] pb-2 font-medium text-right">Quizzes</th>
                      <th className="w-[10%] pb-2 font-medium text-right">Produkte</th>
                      <th
                        className="w-[20%] cursor-pointer pb-2 pl-8 font-medium select-none"
                        onClick={handleCreatedAtSort}
                      >
                        <span className="inline-flex items-center gap-1">
                          Erstellt
                          {sortOrder === 'asc' ? (
                            <ChevronUp className="h-3 w-3" />
                          ) : (
                            <ChevronDown className="h-3 w-3" />
                          )}
                        </span>
                      </th>
                      <th className="w-[6%] pb-2 font-medium text-right">Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.tenants.map((tenant) => (
                      <tr key={tenant.id} className="border-b last:border-0">
                        <td className="py-2">
                          <Link
                            to={`/platform/tenants/${tenant.id}`}
                            className="block truncate font-medium text-primary hover:underline"
                          >
                            {tenant.name}
                          </Link>
                          <div className="truncate text-xs text-muted-foreground">{tenant.slug}</div>
                        </td>
                        <td className="py-2">{TIER_LABELS[tenant.subscriptionTier] ?? tenant.subscriptionTier}</td>
                        <td className="py-2">
                          <span
                            className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[tenant.status] ?? 'bg-muted text-muted-foreground'}`}
                          >
                            {tenant.status}
                          </span>
                        </td>
                        <td className="py-2 text-right tabular-nums">{formatNumber(tenant.userCount)}</td>
                        <td className="py-2 text-right tabular-nums">{formatNumber(tenant.quizCount)}</td>
                        <td className="py-2 text-right tabular-nums">{formatNumber(tenant.productCount)}</td>
                        <td className="py-2 pl-8 text-muted-foreground">{formatDate(tenant.createdAt)}</td>
                        <td className="py-2 text-right">
                          {tenant.slug !== SYSTEM_TENANT_SLUG && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-muted-foreground hover:text-destructive"
                              onClick={() => handleDeleteClick(tenant)}
                              aria-label={`Tenant ${tenant.name} löschen`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={tenantToDelete !== null}
        onOpenChange={(open) => {
          if (!open) handleDeleteCancel();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Tenant löschen?</DialogTitle>
            <DialogDescription>
              {tenantToDelete?.name ?? ''} wird aus der Liste entfernt. Die Daten bleiben erhalten und können wiederhergestellt werden.
            </DialogDescription>
          </DialogHeader>
          {deleteError && (
            <div className="flex items-center gap-2 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              <AlertTriangle className="h-4 w-4" />
              <span>{deleteError}</span>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleDeleteCancel}
              disabled={deleteMutation.isPending}
            >
              Abbrechen
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Lösche…' : 'Löschen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pagination */}
      {data && totalPages > 0 && (
        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Seite {data.page} von {totalPages}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              <ChevronLeft className="mr-1 h-4 w-4" />
              Zurück
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Weiter
              <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
